import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import "./App.css";


import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Products from "./components/Products";
import About from "./components/About";
import Categories from "./components/Categories";
import Why from "./components/Why";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import { useHomeContent } from "./hooks/useHomeContent";

const CategoryProducts = lazy(() => import("./components/CategoryProducts"));
const ProductCatalogModal = lazy(() =>
  import("./components/ProductCatalogModal")
);

const PRODUCTS_PATH = "/products";

const isReloadNavigation = () => {
  const navigationEntry = window.performance
    ?.getEntriesByType?.("navigation")
    ?.at?.(0);

  if (navigationEntry?.type) {
    return navigationEntry.type === "reload";
  }

  return window.performance?.navigation?.type === 1;
};

const toSearch = (params) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, value);
    }
  });

  const query = searchParams.toString();
  return query ? `?${query}` : "";
};

const parseCurrentRoute = () => {
  const { pathname, search } = window.location;

  if (pathname !== PRODUCTS_PATH) {
    return { type: "home" };
  }

  const params = new URLSearchParams(search);
  const view = params.get("view") || "catalog";

  if (view === "family") {
    const family = params.get("family");
    return family ? { type: "family", family } : { type: "catalog" };
  }

  if (view === "category") {
    const category = params.get("category");

    if (!category) {
      return { type: "catalog" };
    }

    return {
      type: "category",
      family: params.get("family") || null,
      returnTo: params.get("returnTo") || null,
      category,
      label: params.get("label") || null,
      section: params.get("section") || null,
    };
  }

  if (view === "product") {
    const category = params.get("category");
    const part = params.get("part");

    if (!category || !part) {
      return { type: "catalog" };
    }

    return {
      type: "product",
      family: params.get("family") || null,
      returnTo: params.get("returnTo") || null,
      category,
      label: params.get("label") || null,
      section: params.get("section") || null,
      part,
    };
  }

  return { type: "catalog" };
};

const getInitialRoute = () => {
  if (isReloadNavigation() && window.location.pathname === "/") {
    window.history.replaceState({}, "", "/");
    return { type: "home" };
  }

  return parseCurrentRoute();
};

const buildUrlForRoute = (route) => {
  if (!route || route.type === "home") {
    return "/";
  }

  if (route.type === "catalog") {
    return `${PRODUCTS_PATH}?view=catalog`;
  }

  if (route.type === "family") {
    return `${PRODUCTS_PATH}${toSearch({
      view: "family",
      family: route.family,
    })}`;
  }

  if (route.type === "category") {
    return `${PRODUCTS_PATH}${toSearch({
      view: "category",
      family: route.family,
      returnTo: route.returnTo,
      category: route.category,
      label: route.label,
      section: route.section,
    })}`;
  }

  if (route.type === "product") {
    return `${PRODUCTS_PATH}${toSearch({
      view: "product",
      family: route.family,
      returnTo: route.returnTo,
      category: route.category,
      label: route.label,
      section: route.section,
      part: route.part,
    })}`;
  }

  return "/";
};

const normalizeSelection = (selection) =>
  typeof selection === "string"
    ? { category: selection, label: selection, section: null }
    : {
        category: selection?.category || "",
        label: selection?.label || selection?.category || "",
        section: selection?.section || null,
      };

function App() {
  const [currentRoute, setCurrentRoute] = useState(() => getInitialRoute());
  const navMenuRequest = null;
  const { content: homeContent, isLoading, error } = useHomeContent();
  const gestureRef = useRef({
    pointerId: null,
    startX: 0,
    direction: null,
  });
  const [historyGesture, setHistoryGesture] = useState({
    visible: false,
    direction: null,
    progress: 0,
  });

  useEffect(() => {
    const syncRoute = () => setCurrentRoute(parseCurrentRoute());

    window.addEventListener("popstate", syncRoute);
    return () => window.removeEventListener("popstate", syncRoute);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [currentRoute]);

  useEffect(() => {
    const activationEdge = 28;
    const commitDistance = 110;

    const resetGesture = () => {
      gestureRef.current = {
        pointerId: null,
        startX: 0,
        direction: null,
      };
      setHistoryGesture({
        visible: false,
        direction: null,
        progress: 0,
      });
    };

    const handlePointerDown = (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) {
        return;
      }

      const viewportWidth = window.innerWidth;
      let direction = null;

      if (event.clientX <= activationEdge) {
        direction = "back";
      } else if (event.clientX >= viewportWidth - activationEdge) {
        direction = "forward";
      }

      if (!direction) {
        return;
      }

      gestureRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        direction,
      };

      setHistoryGesture({
        visible: true,
        direction,
        progress: 0,
      });
    };

    const handlePointerMove = (event) => {
      const gesture = gestureRef.current;

      if (gesture.pointerId !== event.pointerId || !gesture.direction) {
        return;
      }

      const delta =
        gesture.direction === "back"
          ? event.clientX - gesture.startX
          : gesture.startX - event.clientX;
      const distance = Math.max(0, delta);
      const progress = Math.min(distance / commitDistance, 1);

      setHistoryGesture({
        visible: true,
        direction: gesture.direction,
        progress,
      });
    };

    const handlePointerUp = (event) => {
      const gesture = gestureRef.current;

      if (gesture.pointerId !== event.pointerId || !gesture.direction) {
        return;
      }

      const delta =
        gesture.direction === "back"
          ? event.clientX - gesture.startX
          : gesture.startX - event.clientX;

      const shouldNavigate = delta >= commitDistance;
      const direction = gesture.direction;

      resetGesture();

      if (shouldNavigate) {
        if (direction === "back") {
          window.history.back();
        } else {
          window.history.forward();
        }
      }
    };

    const handlePointerCancel = () => {
      resetGesture();
    };

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerCancel);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerCancel);
    };
  }, []);

  const navigateToRoute = (route, options = {}) => {
    const url = buildUrlForRoute(route);
    const method = options.replace ? "replaceState" : "pushState";

    window.history[method]({}, "", url);
    setCurrentRoute(parseCurrentRoute());
  };

  const navigateHomeToSection = (sectionId = "") => {
    navigateToRoute({ type: "home" });

    if (!sectionId) {
      return;
    }

    window.setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
    }, 0);
  };

  const openFamilyPage = (family) => {
    navigateToRoute({ type: "family", family });
  };

  const openCategoryPage = (selection, family = null, returnTo = null) => {
    const normalized = normalizeSelection(selection);

    navigateToRoute({
      type: "category",
      family,
      returnTo,
      category: normalized.category,
      label: normalized.label,
      section: normalized.section,
    });
  };

  const openProductPage = (selection, family, part, returnTo = null) => {
    const normalized = normalizeSelection(selection);

    navigateToRoute({
      type: "product",
      family,
      returnTo,
      category: normalized.category,
      label: normalized.label,
      section: normalized.section,
      part,
    });
  };

  const currentCategorySelection = useMemo(() => {
    if (currentRoute.type !== "category" && currentRoute.type !== "product") {
      return null;
    }

    return {
      category: currentRoute.category,
      label: currentRoute.label,
      section: currentRoute.section,
    };
  }, [currentRoute]);

  const isCatalogPage = currentRoute.type !== "home";

  return (
    <>
      {historyGesture.visible ? (
        <div className="history-gesture" aria-hidden="true">
          <div
            className={`history-gesture-indicator history-gesture-${historyGesture.direction}`}
            style={{
              opacity: 0.35 + historyGesture.progress * 0.65,
              transform: `translateY(-50%) scale(${0.86 + historyGesture.progress * 0.18})`,
            }}
          >
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {historyGesture.direction === "back" ? (
                <>
                  <path d="M19 12H5" />
                  <path d="M11 5l-7 7 7 7" />
                </>
              ) : (
                <>
                  <path d="M5 12h14" />
                  <path d="M13 5l7 7-7 7" />
                </>
              )}
            </svg>
          </div>
        </div>
      ) : null}
      <Navbar
        onProductsClick={() => navigateHomeToSection("products")}
        onCategorySelect={(category, family, returnTo = "catalog") =>
          openCategoryPage(category, family, returnTo)
        }
        onFamilySelect={openFamilyPage}
        onNavigateHome={navigateHomeToSection}
        navMenuRequest={navMenuRequest}
      />

      {isCatalogPage ? (
        <Suspense fallback={null}>
          {currentRoute.type === "catalog" ? (
            <ProductCatalogModal
              onNavigateHome={() => navigateToRoute({ type: "home" })}
              onOpenCatalog={() => navigateToRoute({ type: "catalog" })}
              onSelectCategory={(category) => openCategoryPage(category)}
            />
          ) : null}

          {currentRoute.type === "family" ? (
            <ProductCatalogModal
              onNavigateHome={() => navigateToRoute({ type: "home" })}
              onOpenCatalog={() => navigateToRoute({ type: "catalog" })}
              onSelectCategory={(category) =>
                openCategoryPage(category, currentRoute.family)
              }
              columnTitle={currentRoute.family}
              eyebrow="Selected Product"
              title={currentRoute.family}
              subtitle="Select a category to view all products and specifications from the catalog."
            />
          ) : null}

          {currentRoute.type === "category" || currentRoute.type === "product" ? (
            <CategoryProducts
              category={currentCategorySelection}
              familyTitle={currentRoute.family}
              selectedProductPart={
                currentRoute.type === "product" ? currentRoute.part : null
              }
              onNavigateHome={() => navigateToRoute({ type: "home" })}
              onOpenCatalog={() => navigateToRoute({ type: "catalog" })}
              onOpenFamily={(family) => openFamilyPage(family)}
              onOpenCategory={() =>
                openCategoryPage(
                  currentCategorySelection,
                  currentRoute.family,
                  currentRoute.returnTo
                )
              }
              onSelectProduct={(product) =>
                openProductPage(
                  currentCategorySelection,
                  currentRoute.family,
                  product.partNumber,
                  currentRoute.returnTo
                )
              }
            />
          ) : null}
          <Footer content={homeContent?.footer} />
          <WhatsAppButton />
        </Suspense>
      ) : (
        <>
          {isLoading ? (
            <section className="section-wrap">
              <div className="admin-message">Loading website content...</div>
            </section>
          ) : null}
          {error ? (
            <section className="section-wrap">
              <div className="admin-message is-error">{error}</div>
            </section>
          ) : null}
          <Hero content={homeContent?.hero} />
          <div className="ticker-wrap" aria-hidden="true">
            <div className="ticker-track">
              {(homeContent?.tickerItems || []).map((item, index) => (
                <span className="ticker-item" key={`${item}-${index}`}>
                  {item}
                </span>
              ))}
            </div>
          </div>
          <Products
            onBrowseFamily={openFamilyPage}
            content={homeContent?.productsSection}
          />
          <About content={homeContent?.about} />
          <Categories
            onSelectCategory={(category) => openCategoryPage(category)}
            content={homeContent?.categoriesSection}
          />
          <Why content={homeContent?.why} />
          <Contact content={homeContent?.contact} />
          <Footer content={homeContent?.footer} />
          <WhatsAppButton />
        </>
      )}
    </>
  );
}

export default App;
